import { useMemo, useState } from 'react'
import { Confirm, Notify } from 'notiflix'
import { FaPlus } from 'react-icons/fa'
import api from '../../../../helpers/apiRequester'
import shared from '../shared/mhlShared.module.scss'
import styles from './MilitaryHearingLossUsers.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { MhlAgent, MhlTeam } from '../shared/mhlTypes'

interface Props {
    teams: MhlTeam[]
    agents: MhlAgent[]
    onClose: () => void
    onChanged: () => Promise<void>
}

const errorMessage = (err: any, fallback: string) => err?.response?.data?.message || err?.message || fallback

/**
 * Create / rename / assign a leader / archive / delete teams. Teams bound what
 * agents can see and a leader always belongs to the team they lead (picking a
 * leader moves them in). Deleting a team leaves its agents unassigned.
 */
export const TeamsModal = ({ teams, agents, onClose, onChanged }: Props) => {
    const [newName, setNewName] = useState('')
    const [newLeader, setNewLeader] = useState('')
    const [busy, setBusy] = useState<string | null>(null)
    const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null)
    const [error, setError] = useState<string | null>(null)

    // Any non-admin agent can be made a team's leader; choosing one moves them into that team.
    const leaders = useMemo(() => agents.filter((a) => a.role !== 'admin' && a.cognito_id), [agents])
    const leaderOptions = (teamId: number | null) =>
        [...leaders].sort((a, b) => {
            const aIn = a.team_id === teamId ? 0 : 1
            const bIn = b.team_id === teamId ? 0 : 1
            return aIn - bIn || (a.name || a.email).localeCompare(b.name || b.email)
        })
    const nameOf = (cognitoId: string | null) => (cognitoId ? agents.find((a) => a.cognito_id === cognitoId)?.name || 'Unknown user' : null)

    const run = async (key: string, fn: () => Promise<void>, fail: string) => {
        setBusy(key)
        setError(null)
        try {
            await fn()
            await onChanged()
        } catch (err: any) {
            setError(errorMessage(err, fail))
        } finally {
            setBusy(null)
        }
    }

    const create = (e: React.FormEvent) => {
        e.preventDefault()
        if (newName.trim().length < 2) return setError('Team name needs at least 2 characters.')
        void run(
            '__create',
            async () => {
                const res = await api({ url: '/military-hearing-loss-teams-create', data: { name: newName.trim(), teamLeaderCognitoId: newLeader || null } })
                if (!res.data?.success) throw new Error(res.data?.message)
                setNewName('')
                setNewLeader('')
                Notify.success('Team created')
            },
            'Failed to create team',
        )
    }

    const update = (id: number, data: Partial<{ name: string; teamLeaderCognitoId: string | null; active: boolean }>, ok: string) =>
        run(
            String(id),
            async () => {
                const res = await api({ url: '/military-hearing-loss-teams-update', data: { id, ...data } })
                if (!res.data?.success) throw new Error(res.data?.message)
                setRenaming(null)
                Notify.success(ok)
            },
            'Failed to update team',
        )

    const remove = (t: MhlTeam) =>
        Confirm.show(
            'Delete team',
            `Delete “${t.name}”? ${t.member_count ? `${t.member_count} agent${t.member_count === 1 ? '' : 's'} will be left without a team.` : 'It has no members.'} Client records are unaffected.`,
            'Delete',
            'Cancel',
            () =>
                void run(
                    String(t.id),
                    async () => {
                        const res = await api({ url: '/military-hearing-loss-teams-delete', data: { id: t.id } })
                        if (!res.data?.success) throw new Error(res.data?.message)
                        Notify.success('Team deleted')
                    },
                    'Failed to delete team',
                ),
        )

    return (
        <div className={styles.modalOverlay} onClick={onClose} role="presentation">
            <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h3>Teams</h3>
                        <p className={styles.modalSub}>Group agents for reporting and volume tracking. Teams never restrict what an agent can see.</p>
                    </div>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <form onSubmit={create} className={styles.teamCreateRow}>
                        <label className={styles.field} style={{ flex: 2 }}>
                            <span>New team</span>
                            <input className={shared.searchInput} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. North West" disabled={busy === '__create'} />
                        </label>
                        <label className={styles.field} style={{ flex: 2 }}>
                            <span>Team leader (optional)</span>
                            <select className={shared.select} value={newLeader} onChange={(e) => setNewLeader(e.target.value)} disabled={busy === '__create'}>
                                <option value="">—</option>
                                {leaderOptions(null).map((l) => (
                                    <option key={l.cognito_id!} value={l.cognito_id!}>
                                        {l.name || l.email}
                                        {l.team_name ? ` · ${l.team_name}` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <button type="submit" className={`${shared.btn} ${shared.btnPrimary}`} disabled={busy === '__create' || newName.trim().length < 2} style={{ alignSelf: 'flex-end' }}>
                            <FaPlus size={11} /> Add
                        </button>
                    </form>

                    {error && <div className={styles.formError}>{error}</div>}

                    {teams.length === 0 ? (
                        <p className={shared.emptyStateSmall}>No teams yet. Create one above, then assign agents from the Team column or the edit dialog.</p>
                    ) : (
                        <div className={shared.tableWrap}>
                            <table className={`${shared.table} ${styles.teamsTable}`}>
                                <thead>
                                    <tr>
                                        <th>Team</th>
                                        <th>Leader</th>
                                        <th className={shared.num}>Members</th>
                                        <th>Status</th>
                                        <th className={styles.actionsCol}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {teams.map((t) => {
                                        const isBusy = busy === String(t.id)
                                        const active = Number(t.active) === 1
                                        return (
                                            <tr key={t.id} className={!active ? styles.rowDisabled : undefined}>
                                                <td>
                                                    {renaming?.id === t.id ? (
                                                        <form
                                                            className={styles.inlineForm}
                                                            onSubmit={(e) => {
                                                                e.preventDefault()
                                                                if (renaming.name.trim().length >= 2) void update(t.id, { name: renaming.name.trim() }, 'Team renamed')
                                                            }}
                                                        >
                                                            <input className={shared.searchInput} value={renaming.name} autoFocus onChange={(e) => setRenaming({ id: t.id, name: e.target.value })} disabled={isBusy} />
                                                            <button type="submit" className={`${shared.btn} ${shared.btnSmall} ${shared.btnPrimary}`} disabled={isBusy}>
                                                                Save
                                                            </button>
                                                            <button type="button" className={`${shared.btn} ${shared.btnSmall}`} onClick={() => setRenaming(null)} disabled={isBusy}>
                                                                Cancel
                                                            </button>
                                                        </form>
                                                    ) : (
                                                        <span className={shared.stackPrimary}>{t.name}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <select
                                                        className={`${shared.select} ${styles.inlineSelect} ${!t.team_leader_cognito_id ? styles.inlineSelectEmpty : ''}`}
                                                        value={t.team_leader_cognito_id || ''}
                                                        disabled={isBusy}
                                                        onChange={(e) => void update(t.id, { teamLeaderCognitoId: e.target.value || null }, 'Team leader updated')}
                                                    >
                                                        <option value="">No leader</option>
                                                        {leaderOptions(t.id).map((l) => (
                                                            <option key={l.cognito_id!} value={l.cognito_id!}>
                                                                {l.name || l.email}
                                                                {l.team_id === t.id ? '' : l.team_name ? ` · moves from ${l.team_name}` : ' · joins team'}
                                                            </option>
                                                        ))}
                                                        {t.team_leader_cognito_id && !leaders.some((l) => l.cognito_id === t.team_leader_cognito_id) && (
                                                            <option value={t.team_leader_cognito_id}>{nameOf(t.team_leader_cognito_id)}</option>
                                                        )}
                                                    </select>
                                                </td>
                                                <td className={shared.num}>{t.member_count}</td>
                                                <td>
                                                    <Pill tone={active ? 'good' : 'muted'} dot>
                                                        {active ? 'Active' : 'Archived'}
                                                    </Pill>
                                                </td>
                                                <td className={styles.actionsCol}>
                                                    <div className={styles.rowActions}>
                                                        <button type="button" className={`${shared.btn} ${shared.btnSmall}`} disabled={isBusy} onClick={() => setRenaming({ id: t.id, name: t.name })}>
                                                            Rename
                                                        </button>
                                                        <button type="button" className={`${shared.btn} ${shared.btnSmall}`} disabled={isBusy} onClick={() => void update(t.id, { active: !active }, active ? 'Team archived' : 'Team restored')}>
                                                            {active ? 'Archive' : 'Restore'}
                                                        </button>
                                                        <button type="button" className={`${shared.btn} ${shared.btnSmall} ${shared.btnDanger}`} disabled={isBusy} onClick={() => remove(t)}>
                                                            Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <button type="button" className={`${shared.btn} ${shared.btnPrimary}`} onClick={onClose}>
                        Done
                    </button>
                </div>
            </div>
        </div>
    )
}
